import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle, Clock, Zap, Calendar, Shield, Users, Star, Phone, Mail } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useEffect } from "react";
import Header from "./components/Header";
import Breadcrumbs from "./components/Breadcrumbs";
import GlobalFooter from "./components/GlobalFooter";
import StickyMobileCTA from "./components/StickyMobileCTA";

// Cal.com booking link
const CALENDLY_LINK = "https://cal.com/modern-path-ai-pludn4";

// Animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

// Complete industry data with specialized SEO content
const industryData = {
  "appliance-repair": {
    title: "Appliance Repair",
    slug: "appliance-repair",
    heroTitle: "24/7 AI Voice Dispatch for Appliance Repair Businesses",
    heroSubtitle: "Never miss a broken appliance call again. Our AI answers in 2 seconds, identifies the issue, and books the service call while you're finishing another repair.",
    seoCopy: `In the appliance repair industry, a missed call is a missed job. Whether it's a fridge failing in Richmond Hill or a dryer repair in Scarborough, customers call the first person who answers. Modern Path AI provides a 24/7 AI Receptionist that understands technical appliance issues. Our AI qualifies the lead (brand, model, and symptoms), checks your real-time availability, and books the service call directly into your calendar. Stop losing leads to 'no-answers' after hours. Our system handles emergency repair inquiries with the same empathy and precision as a human dispatcher, ensuring your technicians stay busy and your overhead stays low.

When a homeowner's refrigerator stops cooling at midnight, they're not leaving a voicemail—they're calling until someone answers. With Modern Path AI, you become that someone, 24 hours a day, 7 days a week. Our voice AI is trained specifically for appliance repair terminology, understanding the difference between a compressor issue and a thermostat malfunction. This technical knowledge allows us to collect the right information upfront, so your technicians arrive prepared with the correct parts.

The cost savings are substantial. Traditional answering services charge per minute and only take messages. Modern Path AI actively books jobs at a fixed monthly rate. Most appliance repair businesses report a 35% increase in after-hours bookings within the first month, directly translating to additional revenue that would have otherwise gone to competitors.`,
    painHeadline: "A missed call is a missed job.",
    painCopy: "When someone's fridge stops cooling or their washer floods the laundry room, they call the first repair company that answers. Every voicemail is revenue walking to your competitor.",
    leadType: "Service Call",
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1200&q=80",
    metaTitle: "Appliance Repair AI Voice Agents & Automation | Modern Path AI",
    metaDescription: "Boost your Appliance Repair business with 24/7 AI Voice Agents. Automate scheduling, qualify leads, and never miss a call again in the GTA.",
    commonInquiries: [
      "Refrigerator not cooling",
      "Freezer temperature issues",
      "Washing machine leaking",
      "Dryer not heating",
      "Dishwasher won't drain",
      "Oven not heating evenly",
      "Ice maker repair",
      "Garbage disposal replacement",
      "Microwave repair",
      "Stove burner issues",
      "Washer spin cycle problems",
      "Appliance error codes"
    ],
    useCases: [
      { title: "Brand & Model Collection", description: "AI identifies appliance type, brand, model number, and purchase date to prepare your tech with the right parts.", icon: CheckCircle },
      { title: "Symptom-Based Triage", description: "Understands technical symptoms—not cooling, leaking, error codes—to prioritize emergencies and schedule appropriately.", icon: Zap },
      { title: "Real-Time Booking", description: "Checks your actual availability and books service calls directly into your calendar or ServiceTitan.", icon: Calendar },
      { title: "Warranty Verification", description: "Collects warranty information upfront to set proper customer expectations before the appointment.", icon: Shield }
    ],
    faq: [
      { q: "How does the AI handle technical appliance questions?", a: "Our AI is trained on appliance repair terminology and common failure patterns. It asks about symptoms, error codes, brand, and model to help your technicians arrive prepared." },
      { q: "Can it integrate with my scheduling software?", a: "Yes! Modern Path AI integrates with ServiceTitan, Housecall Pro, Jobber, and calendar systems like Google Calendar for seamless job scheduling." },
      { q: "Will customers trust an AI for appliance emergencies?", a: "Customers want help fast. When their freezer is thawing, an instant AI response beats waiting on hold or leaving a voicemail that won't be returned until morning." },
      { q: "What's the ROI compared to an answering service?", a: "Traditional services take messages; we book jobs. Most clients see 35% more after-hours bookings within the first month at a lower cost than per-minute answering services." },
      { q: "How does pricing work?", a: "We offer fixed monthly pricing with no per-minute charges. This means predictable costs regardless of call volume during busy seasons." }
    ]
  },
  "home-services": {
    title: "HVAC & Plumbing",
    slug: "home-services",
    heroTitle: "Emergency HVAC & Plumbing AI Dispatch – Never Miss a Pipe Burst",
    heroSubtitle: "When a furnace goes out in winter or a pipe bursts at 3 AM, customers won't wait. Modern Path AI acts as your front-line emergency response.",
    seoCopy: `HVAC and Plumbing leads are high-stakes and high-value. When a furnace goes out in the dead of a GTA winter or a pipe bursts at 3 AM, customers won't wait for a callback. Modern Path AI acts as your front-line emergency response. Our Voice AI identifies the urgency of the call, captures the customer's location and equipment details, and provides immediate confirmation that help is on the way. We integrate with your dispatch software to ensure that high-priority emergency leads are flagged instantly, allowing your team to focus on the tools while the AI focuses on the phones.

The home services industry operates on urgency. A homeowner with no heat in January isn't comparing quotes—they're hiring the first company that answers and confirms dispatch. Modern Path AI ensures you're always that company. Our AI understands HVAC terminology, from "furnace won't ignite" to "AC compressor noise," and asks the right follow-up questions to assess urgency and prepare your technicians.

For plumbing emergencies, every minute counts. Our AI immediately determines if there's active flooding, guides the customer to shut off the main water valve if safe, and prioritizes the dispatch. This proactive approach not only wins the job but builds customer loyalty. Many HVAC and plumbing businesses report that after-hours calls convert at 40% higher rates because customers appreciate immediate, professional response when they're stressed.

The financial impact is significant. Emergency service calls command premium rates, and capturing those calls 24/7 can add $50,000 or more in annual revenue for a typical HVAC/plumbing contractor.`,
    painHeadline: "In emergencies, the first to answer wins the job.",
    painCopy: "No heat in winter. Burst pipe at 3 AM. These high-value emergency calls go to whoever answers first. Every missed call is premium revenue lost.",
    leadType: "Emergency Dispatch",
    image: "https://images.unsplash.com/photo-1585704032915-c3400ca199e7?w=1200&q=80",
    metaTitle: "HVAC & Plumbing AI Voice Agents & Automation | Modern Path AI",
    metaDescription: "Boost your HVAC & Plumbing business with 24/7 AI Voice Agents. Automate scheduling, qualify leads, and never miss a call again in the GTA.",
    commonInquiries: [
      "Furnace not heating",
      "AC not cooling",
      "Burst pipe emergency",
      "Water heater replacement",
      "Drain cleaning",
      "Toilet repair",
      "Clogged sewer line",
      "Thermostat issues",
      "Heat pump service",
      "Boiler repair",
      "Gas line installation",
      "Tankless water heater"
    ],
    useCases: [
      { title: "Emergency Triage", description: "AI determines true emergencies—active flooding, no heat, gas smell—vs. routine maintenance for proper dispatch priority.", icon: Zap },
      { title: "Equipment Details", description: "Collects furnace/AC brand, age, and symptoms to prepare technicians with likely parts and diagnostic approach.", icon: CheckCircle },
      { title: "ServiceTitan Integration", description: "Syncs with ServiceTitan, Housecall Pro, and other dispatch software for seamless scheduling and job creation.", icon: Calendar },
      { title: "After-Hours Premium", description: "Captures 40% of emergency calls that come outside business hours—high-value leads competitors miss.", icon: Clock }
    ],
    faq: [
      { q: "How does the AI handle true plumbing emergencies?", a: "Our AI is trained to identify critical situations. For active flooding, it guides customers to shut off water if safe while confirming emergency dispatch, potentially minimizing damage." },
      { q: "Can it distinguish between HVAC maintenance and emergencies?", a: "Yes. The AI asks about symptoms, timeline, and conditions to determine if it's a no-heat emergency requiring immediate dispatch or routine maintenance that can be scheduled normally." },
      { q: "What about furnace emergencies in winter?", a: "No-heat calls in winter are automatically flagged as high priority. The AI confirms immediate dispatch and can provide the customer with an estimated arrival time." },
      { q: "How much additional revenue can I expect?", a: "Most HVAC/plumbing contractors capture $50,000+ in additional annual revenue from after-hours emergency calls they previously missed." },
      { q: "Does it integrate with my dispatch software?", a: "Yes! We integrate with ServiceTitan, Housecall Pro, Jobber, FieldEdge, and other popular HVAC/plumbing software platforms." }
    ]
  },
  "legal": {
    title: "Law Firms",
    slug: "legal",
    heroTitle: "AI Legal Intake Agents for Law Firms – Qualify Leads 24/7",
    heroSubtitle: "Streamline your intake process. Our AI agents gather case details, conflict-of-interest data, and contact information with professional-grade accuracy.",
    seoCopy: `For law firms, lead qualification is the bottleneck of growth. Modern Path AI streamlines the intake process by handling the initial consultation inquiries. Our AI agents are trained to gather essential case details, conflict-of-interest data, and contact information with professional-grade accuracy. By filtering out non-viable leads and booking qualified consultations directly for your associates, we save your firm dozens of billable hours every month. Ensure your firm is 'always open' for new clients without the massive overhead of a 24/7 call center.

Legal emergencies don't follow business hours. Car accidents happen at 2 AM. Arrests occur on weekends. DUI charges come at midnight. The law firm that answers first and begins intake immediately has a significant advantage in securing the client. Modern Path AI ensures your firm never misses these high-value opportunities.

Our AI understands legal intake requirements. It collects case type, incident details, statute of limitations concerns, and all parties involved for conflict checks. For personal injury, it gathers accident details, injury extent, and insurance information. For criminal defense, it captures charge details and court dates. This comprehensive intake means your attorneys can immediately assess case viability.

The ROI is compelling. Traditional 24/7 legal answering services cost $500-$1500/month and only take messages. Modern Path AI completes full intake at a competitive price point. More importantly, the speed of response directly correlates with client conversion. Potential clients contacting multiple firms typically hire whoever provides the fastest, most professional response.`,
    painHeadline: "Lead qualification is the bottleneck of growth.",
    commonInquiries: [
      "Personal injury consultation",
      "Car accident claim",
      "Workers compensation",
      "Criminal defense inquiry",
      "DUI/DWI representation",
      "Family law consultation",
      "Divorce filing",
      "Child custody matters",
      "Estate planning",
      "Business litigation",
      "Real estate disputes",
      "Immigration services"
    ],
    painCopy: "Legal emergencies don't wait for business hours. Car accidents at 2 AM, arrests on weekends—the firm that answers first and completes intake wins the case.",
    leadType: "Case Inquiry",
    image: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200&q=80",
    metaTitle: "Law Firm AI Voice Agents & Legal Intake Automation | Modern Path AI",
    metaDescription: "Boost your Law Firm business with 24/7 AI Voice Agents. Automate legal intake, qualify leads, and never miss a call again in the GTA.",
    useCases: [
      { title: "Complete Legal Intake", description: "AI collects case type, incident details, injuries, timeline, and all parties for immediate conflict checking.", icon: CheckCircle },
      { title: "Practice Area Routing", description: "Identifies personal injury, criminal defense, family law, or business matters to route to the appropriate attorney.", icon: Users },
      { title: "24/7 Case Capture", description: "Legal emergencies happen around the clock. Capture leads when accidents and arrests actually occur.", icon: Clock },
      { title: "Conflict Check Prep", description: "Gathers all party names—plaintiff, defendant, insurance companies—so your team can run conflicts immediately.", icon: Shield }
    ],
    faq: [
      { q: "How does the AI handle sensitive legal inquiries?", a: "Our AI is trained to gather essential case information professionally and confidentially—incident dates, parties involved, injuries—without providing legal advice or making outcome promises." },
      { q: "Can it differentiate between practice areas?", a: "Yes! The AI asks qualifying questions to identify personal injury, family law, criminal defense, estate planning, and business law matters, routing each appropriately." },
      { q: "Will potential clients trust an AI for legal matters?", a: "Clients in legal emergencies need immediate help. An AI that answers at 2 AM and confirms their case will be reviewed beats a voicemail that won't be heard until Monday." },
      { q: "How does this compare to a legal answering service?", a: "Traditional services take messages; our AI completes full intake with all details needed for conflict checks and case evaluation, saving hours of follow-up." },
      { q: "Does it integrate with legal practice management software?", a: "Modern Path AI integrates with Clio, MyCase, PracticePanther, and other legal software for seamless case management." }
    ]
  },
  "garage-door": {
    title: "Garage Door Services",
    slug: "garage-door",
    heroTitle: "24/7 AI Dispatch for Garage Door Emergencies",
    heroSubtitle: "When someone's car is trapped or their door won't secure, our AI answers instantly and dispatches your team—even at 2 AM.",
    seoCopy: `Garage door emergencies don't happen on a schedule. A broken spring at 6 AM means someone can't get to work. A door that won't close at midnight is a security emergency. Modern Path AI ensures your garage door company is always available to capture these urgent, high-value service calls.

Our AI understands garage door terminology and common failure modes. When a customer describes a "loud bang and the door won't open," the AI recognizes a likely spring failure, advises against DIY attempts for safety, and prioritizes same-day emergency dispatch. This technical knowledge builds customer confidence and ensures your technicians arrive prepared.

The garage door industry has a unique challenge: most calls are emergencies requiring immediate response. Unlike other home services where customers might shop around, a homeowner with a broken garage door needs help NOW. Modern Path AI ensures you're the company that answers and confirms dispatch while competitors go to voicemail.

For commercial clients with loading dock doors or property managers with multiple units, our AI handles multi-location scheduling, collects proper authorization, and manages the complexity of commercial accounts. This capability opens additional revenue streams many garage door companies miss.

The economics are compelling. Emergency garage door calls typically command $300-$800 in revenue. Capturing just 5 additional emergency calls per month that would have gone to voicemail adds $18,000-$48,000 in annual revenue—far exceeding the cost of 24/7 AI coverage.`,
    painHeadline: "Garage door emergencies don't happen 9-to-5.",
    painCopy: "A broken spring at 6 AM, a door that won't close at midnight—these high-margin emergency calls go to whoever answers first.",
    leadType: "Emergency Repair",
    image: "https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=1200&q=80",
    metaTitle: "Garage Door Services AI Voice Agents & Automation | Modern Path AI",
    metaDescription: "Boost your Garage Door business with 24/7 AI Voice Agents. Automate scheduling, qualify leads, and never miss a call again in the GTA.",
    commonInquiries: [
      "Broken garage door spring",
      "Garage door won't open",
      "Garage door off track",
      "Garage door opener repair",
      "Remote not working",
      "Door won't close completely",
      "Loud grinding noise",
      "Cable replacement",
      "Sensor alignment",
      "Emergency garage door repair",
      "Commercial door service",
      "New garage door installation"
    ],
    useCases: [
      { title: "Emergency Triage", description: "AI determines if it's a security issue (door won't close), safety hazard (broken spring), or routine maintenance.", icon: Zap },
      { title: "Problem Diagnosis", description: "Asks about symptoms—loud bang, door off track, opener not responding—to dispatch with the right parts.", icon: CheckCircle },
      { title: "Same-Day Scheduling", description: "For emergencies, offers same-day or next-morning slots based on your actual technician availability.", icon: Calendar },
      { title: "Commercial Accounts", description: "Handles commercial loading dock doors and property managers with multiple locations.", icon: Shield }
    ],
    faq: [
      { q: "How does the AI handle broken spring emergencies?", a: "When a caller describes a loud bang and door won't open, the AI recognizes a spring failure, warns against DIY attempts, and prioritizes same-day dispatch given the safety risk." },
      { q: "Can it distinguish between opener and door problems?", a: "Yes! The AI asks diagnostic questions: 'Does the motor run but door not move?' 'Can you lift it manually?' This determines if it's an opener, spring, or track issue." },
      { q: "What about after-hours security emergencies?", a: "A door that won't close is flagged as a security emergency. The AI confirms immediate dispatch and can provide estimated arrival time to reassure the customer." },
      { q: "How does it handle commercial clients?", a: "The AI manages commercial accounts, collecting property manager info, location details, and urgency level for loading dock or multi-unit properties." },
      { q: "What's the ROI on emergency calls?", a: "Emergency garage door calls average $300-$800. Capturing 5 more per month adds $18,000-$48,000 annually—well beyond the cost of AI coverage." }
    ]
  },
  "locksmith": {
    title: "Locksmith Services",
    slug: "locksmith",
    heroTitle: "24/7 AI Dispatch for Emergency Lockout Calls",
    heroSubtitle: "When someone's locked out at midnight, our AI answers instantly, collects the location, and dispatches your team while competitors go to voicemail.",
    seoCopy: `Locksmith services are defined by urgency. A parent locked out with kids in the car isn't leaving a voicemail—they're calling until someone answers. A business owner locked out before opening isn't waiting for a callback. Modern Path AI ensures you capture these high-margin emergency calls 24/7.

Our AI is optimized for locksmith dispatch efficiency. It immediately collects exact location—highway mile marker, cross streets, parking lot name—for fastest dispatch. It determines lock type (automotive, residential, commercial) so you dispatch the right technician with appropriate tools and key blanks. For automotive lockouts, it gathers year, make, and model to confirm you can service the vehicle.

Safety emergencies receive special handling. When children or pets are locked in a vehicle, the AI flags the call as critical priority and can connect directly to your on-call technician while simultaneously creating the dispatch. This life-safety protocol differentiates professional locksmith services.

The locksmith industry faces unique competitive pressure—customers call multiple companies and hire whoever arrives first. Modern Path AI ensures you're always the first to answer AND the first to confirm dispatch with an ETA. This speed advantage converts at significantly higher rates than callbacks.

Financially, locksmith emergency calls typically range from $75 for simple lockouts to $300+ for automotive transponder keys. After-hours calls command premium rates. Capturing these calls 24/7 while maintaining low overhead through AI dispatch creates substantial profit margins.`,
    painHeadline: "Lockouts are pure urgency—every minute counts.",
    painCopy: "A parent locked out with kids in the car isn't leaving a voicemail. They're calling until someone answers and confirms help is coming.",
    leadType: "Emergency Lockout",
    image: "https://images.unsplash.com/photo-1582139329536-e7284fece509?w=1200&q=80",
    metaTitle: "Locksmith AI Voice Agents & Automation | Modern Path AI",
    metaDescription: "Boost your Locksmith business with 24/7 AI Voice Agents. Automate dispatch, qualify leads, and never miss a call again in the GTA.",
    commonInquiries: [
      "Locked out of house",
      "Car lockout service",
      "Lost car keys",
      "Lock rekey service",
      "Broken key extraction",
      "Smart lock installation",
      "Commercial lock change",
      "Transponder key programming",
      "Emergency lockout",
      "Safe opening",
      "Deadbolt installation",
      "Master key system"
    ],
    useCases: [
      { title: "Instant Location Collection", description: "AI collects exact address, cross streets, and identifying details for fastest dispatch to the lockout.", icon: Zap },
      { title: "Lock Type Identification", description: "Determines automotive, residential, or commercial to dispatch with the right tools and key blanks.", icon: CheckCircle },
      { title: "Safety Emergency Protocol", description: "Children/pets locked in vehicles are flagged critical and can connect directly to on-call technician.", icon: Shield },
      { title: "ETA Communication", description: "Provides callers with estimated arrival based on technician location and current jobs.", icon: Clock }
    ],
    faq: [
      { q: "How does the AI handle car lockouts with kids inside?", a: "The AI identifies life-safety emergencies immediately. When children or pets are locked in a vehicle, it flags critical priority and can connect directly to your on-call tech." },
      { q: "Can it collect vehicle information for automotive lockouts?", a: "Yes! It asks for year, make, model, and location to confirm you have the right tools and key blanks before dispatching." },
      { q: "Will customers trust an AI when stressed and locked out?", a: "Locked-out customers want help NOW. An instant AI answer with confirmed ETA beats waiting on hold or leaving a voicemail every time." },
      { q: "What about commercial lockout and rekey jobs?", a: "The AI handles commercial clients differently, collecting business name, authorized contact, and scope for larger rekey or access control projects." },
      { q: "How does pricing compare to 24/7 dispatch staff?", a: "A single overnight dispatcher costs $50,000+ per year. Modern Path AI provides round-the-clock coverage at a fraction of the cost." }
    ]
  },
  "pest-control": {
    title: "Pest Control",
    slug: "pest-control",
    heroTitle: "24/7 AI Booking for Pest Control Services",
    heroSubtitle: "When someone finds a scorpion in their baby's room or mice in their kitchen, our AI answers instantly and books the inspection while you're treating another property.",
    seoCopy: `Pest problems feel urgent to homeowners. When someone sees a rat, discovers bed bugs, or finds a wasp nest by their door, they're calling every pest control company on Google until someone answers and confirms an inspection. Modern Path AI ensures you're always the company that answers first.

Our AI understands pest control terminology and can distinguish between situations requiring immediate treatment versus routine prevention. For infestations—rodents, bed bugs, cockroaches—the AI prioritizes same-day or next-day inspections. For preventive treatments and seasonal services, it schedules according to your route efficiency.

The pest control industry has significant upsell opportunity that AI captures effectively. When booking an initial inspection, the AI introduces your recurring prevention plans, increasing customer lifetime value from the first interaction. It also collects property details—square footage, type, problem areas—to prepare accurate quotes.

For commercial accounts—restaurants, hotels, warehouses—the AI gathers compliance information, property manager contacts, and service frequency requirements. These larger accounts often call after hours when pest issues arise and expect immediate professional response.

Seasonality creates call volume spikes in pest control. Spring brings carpenter ants and termites; summer brings wasps and mosquitoes; fall brings rodent intrusion. Modern Path AI handles unlimited concurrent calls, ensuring you capture every lead during busy seasons without hiring temporary staff.

The math is simple: the average pest control customer is worth $400-$800 annually with recurring service. Every captured lead that becomes a recurring customer compounds your revenue growth.`,
    painHeadline: "Pest problems feel urgent—your response should match.",
    painCopy: "When a homeowner sees a rat, they're calling until someone answers. They want confirmation NOW that help is coming, not a callback tomorrow.",
    leadType: "Pest Inspection",
    image: "https://images.unsplash.com/photo-1563453392212-326f5e854473?w=1200&q=80",
    metaTitle: "Pest Control AI Voice Agents & Automation | Modern Path AI",
    metaDescription: "Boost your Pest Control business with 24/7 AI Voice Agents. Automate scheduling, qualify leads, and never miss a call again in the GTA.",
    commonInquiries: [
      "Mice infestation",
      "Rat removal",
      "Bed bug treatment",
      "Cockroach extermination",
      "Ant problem",
      "Wasp nest removal",
      "Termite inspection",
      "Spider control",
      "Rodent prevention",
      "Flea treatment",
      "Wildlife removal",
      "Monthly pest prevention"
    ],
    useCases: [
      { title: "Pest Identification", description: "AI asks targeted questions to identify likely pest—rodent droppings, insect sightings, sounds—for proper treatment prep.", icon: CheckCircle },
      { title: "Urgency Assessment", description: "Determines active infestation requiring same-day service vs. preventive inspection that can be scheduled normally.", icon: Zap },
      { title: "Recurring Service Upsell", description: "Introduces prevention plans during initial booking to increase customer lifetime value from first contact.", icon: Star },
      { title: "Commercial Accounts", description: "Handles restaurants, hotels, and warehouses with compliance requirements and property manager contacts.", icon: Shield }
    ],
    faq: [
      { q: "How does the AI determine pest type from a phone call?", a: "The AI asks about what the customer saw (droppings, insects, sounds), where, and how often—information that helps your techs prepare the right treatments." },
      { q: "Can it handle bed bug inquiries?", a: "Yes! Bed bug calls get special handling—collecting travel history, when bites started, and affected rooms to help your inspector prioritize areas." },
      { q: "What about recurring service plans?", a: "The AI introduces your prevention plans during initial booking, significantly increasing customer lifetime value from the first interaction." },
      { q: "How does it handle seasonal call spikes?", a: "Modern Path AI handles unlimited concurrent calls, so even during spring termite season, everyone gets answered immediately—no lost leads." },
      { q: "What's the lifetime value impact?", a: "Average pest control customers are worth $400-$800 annually with recurring service. Every captured lead compounds your revenue growth." }
    ]
  },
  "landscaping": {
    title: "Landscaping & Lawn Care",
    slug: "landscaping",
    heroTitle: "24/7 AI Booking for Landscaping Companies",
    heroSubtitle: "Never miss a lawn care inquiry or landscaping project lead again. Our AI answers calls while your crews are on job sites, booking estimates and recurring services automatically.",
    seoCopy: `The landscaping industry faces a unique challenge: call volume surges seasonally while crews are busy on job sites. Spring arrives and everyone wants lawn care at once. Modern Path AI ensures you capture every inquiry while your team focuses on the work.

Our AI understands landscaping service categories and can guide callers to the right service. It distinguishes between lawn maintenance, landscape design, hardscaping projects, irrigation, and tree services. For each category, it collects relevant details—property size, current landscaping, project scope—to prepare accurate estimates.

Recurring services are the foundation of landscaping profitability. The AI books weekly, bi-weekly, or monthly lawn maintenance directly into your route schedule. It positions seasonal packages—spring cleanup, fall leaf removal, winterization—to maximize customer value throughout the year.

Commercial and HOA accounts represent significant revenue opportunity. The AI handles property managers with multiple locations, collecting square footage, service frequencies, and bid deadlines for competitive proposals. These accounts often inquire after hours and expect immediate professional response.

The economics of AI booking shine in landscaping. A full-time office person costs $35,000+ annually. Modern Path AI provides 24/7 coverage at a fraction of that cost, handling unlimited calls during spring rush without overtime. More importantly, the AI never takes a lunch break during prime calling hours.

Property owners making landscaping decisions often call multiple companies and hire whoever responds fastest with the most professional interaction. Modern Path AI ensures you're always first and always professional.`,
    painHeadline: "Homeowners make landscaping decisions fast.",
    painCopy: "Spring arrives and everyone wants lawn care at once. A homeowner comparing three companies will hire the one who answers and books an estimate first.",
    leadType: "Service Estimate",
    image: "https://images.unsplash.com/photo-1558904541-efa843a96f01?w=1200&q=80",
    metaTitle: "Landscaping AI Voice Agents & Automation | Modern Path AI",
    metaDescription: "Boost your Landscaping business with 24/7 AI Voice Agents. Automate scheduling, qualify leads, and never miss a call again in the GTA.",
    commonInquiries: [
      "Lawn mowing service",
      "Spring cleanup",
      "Fall leaf removal",
      "Landscape design",
      "Mulch installation",
      "Hedge trimming",
      "Patio installation",
      "Retaining wall",
      "Sod installation",
      "Irrigation system",
      "Tree pruning",
      "Weekly lawn maintenance"
    ],
    useCases: [
      { title: "Service Categorization", description: "AI identifies lawn maintenance, landscape design, hardscaping, or seasonal cleanup for proper crew assignment.", icon: CheckCircle },
      { title: "Property Details", description: "Gathers lot size, current landscaping, and scope of work to prepare accurate estimates before site visits.", icon: Calendar },
      { title: "Recurring Booking", description: "Books weekly, bi-weekly, or monthly services directly into your route schedule with customer details.", icon: Star },
      { title: "Commercial/HOA Handling", description: "Manages property managers with multiple locations, collecting bid deadlines and service requirements.", icon: Shield }
    ],
    faq: [
      { q: "How does the AI handle seasonal call surges?", a: "Modern Path AI handles unlimited concurrent calls, so even when 20 people call about spring cleanup on the first nice day, everyone gets answered." },
      { q: "Can it book recurring lawn care services?", a: "Yes! The AI schedules weekly, bi-weekly, or monthly services and adds them directly to your route schedule with customer address and details." },
      { q: "What about commercial and HOA accounts?", a: "The AI collects property manager contacts, square footage, service frequencies, and bid deadlines for competitive commercial proposals." },
      { q: "How does this compare to hiring seasonal staff?", a: "Seasonal staff require training and may leave mid-season. Modern Path AI provides consistent 24/7 coverage at a fixed monthly cost." },
      { q: "Can it distinguish between service types?", a: "Absolutely. It differentiates lawn maintenance, landscape design, hardscaping, irrigation, and tree services to route appropriately." }
    ]
  },
  "auto-repair": {
    title: "Auto Repair",
    slug: "auto-repair",
    heroTitle: "24/7 AI Booking for Auto Repair Shops",
    heroSubtitle: "Never miss a service appointment while your techs are under the hood. Our AI answers calls, collects vehicle information, and books appointments while your team focuses on repairs.",
    seoCopy: `Auto repair shops face a constant challenge: the phone rings while technicians are elbow-deep in engine bays and service writers are helping customers at the counter. Every missed call is a customer booking with a competitor. Modern Path AI ensures every inquiry becomes an appointment.

Our AI is trained on automotive terminology and collects the information your shop needs. Year, make, model, and current mileage come standard. For service requests, it gathers symptom descriptions—check engine light, strange noises, performance issues—so your techs can prepare diagnostic equipment. For routine maintenance, it confirms service intervals and schedules appropriately.

The service drive experience matters. Customers calling about car problems are often stressed. An AI that answers immediately, asks relevant questions professionally, and confirms their appointment builds confidence in your shop before they even arrive. This first impression sets the tone for the entire service relationship.

Fleet accounts represent significant opportunity for repair shops. Modern Path AI handles fleet managers with multiple vehicles, tracking service history prompts and scheduling regular maintenance. These accounts require professional phone handling but rarely justify dedicated staff—AI is the perfect solution.

The financial impact is straightforward. A missed call that goes to a competitor costs your average repair order—typically $350-$500. Capturing 10 additional calls per week at a 30% conversion rate adds $50,000+ in annual revenue. Modern Path AI pays for itself in the first week of operation.`,
    painHeadline: "When a car breaks down, customers call immediately.",
    painCopy: "A driver with a check engine light isn't waiting for a callback—they're calling shops until someone books them in. Be the shop that always answers.",
    leadType: "Service Appointment",
    image: "https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?w=1200&q=80",
    metaTitle: "Auto Repair AI Voice Agents & Automation | Modern Path AI",
    metaDescription: "Boost your Auto Repair business with 24/7 AI Voice Agents. Automate scheduling, qualify leads, and never miss a call again in the GTA.",
    commonInquiries: [
      "Check engine light on",
      "Oil change service",
      "Brake inspection",
      "Tire rotation",
      "Transmission problems",
      "Car won't start",
      "Strange engine noise",
      "AC not cooling",
      "Battery replacement",
      "Tune-up service",
      "Pre-purchase inspection",
      "Fleet maintenance"
    ],
    useCases: [
      { title: "Vehicle Information", description: "AI collects year, make, model, and mileage upfront so your service advisor has everything before the car arrives.", icon: CheckCircle },
      { title: "Symptom Documentation", description: "Records customer descriptions of issues—noises, warning lights, performance problems—for technician review.", icon: Zap },
      { title: "Appointment Scheduling", description: "Books oil changes, brake inspections, and diagnostic appointments based on your shop's actual availability.", icon: Calendar },
      { title: "Fleet Account Management", description: "Handles fleet customers with multiple vehicles, tracking service history and scheduling regular maintenance.", icon: Shield }
    ],
    faq: [
      { q: "How does the AI collect vehicle information?", a: "It asks for year, make, model, and current mileage in a conversational flow, plus symptoms to help your techs prepare diagnostic equipment." },
      { q: "Can it handle check engine light calls?", a: "Yes! The AI collects vehicle info and symptoms, then books a diagnostic appointment. It can mention if you offer free code reads to encourage booking." },
      { q: "What about fleet and commercial accounts?", a: "The AI manages fleet managers, collecting multiple vehicle information, maintenance schedules, and PO numbers for business accounts." },
      { q: "How does this compare to a full-time service writer?", a: "A service writer costs $40,000+ annually. Modern Path AI handles all incoming calls at a fraction of the cost, freeing staff for in-person customers." },
      { q: "What's the revenue impact?", a: "Capturing 10 additional calls weekly at 30% conversion adds $50,000+ annually at average repair order values. AI pays for itself in a week." }
    ]
  },
  "towing": {
    title: "Towing & Roadside",
    slug: "towing",
    heroTitle: "24/7 AI Dispatch for Towing Companies",
    heroSubtitle: "When someone's stranded on the highway, our AI answers in seconds, collects location, and dispatches the nearest truck—even during your busiest nights.",
    seoCopy: `Towing is 100% urgency. A stranded driver is calling every towing company in their search results—the first one to answer and confirm dispatch gets the job. Modern Path AI ensures you're always first.

Our AI is optimized for emergency dispatch efficiency. It immediately collects exact location—highway name and direction, mile markers or exits, parking lot business names—for fastest dispatch. For accidents, it gathers scene details, number of vehicles, and police presence. This information allows optimal truck assignment and route planning.

Motor club and insurance roadside calls require specific handling. The AI collects member numbers, claim information, and policy details for proper billing. It understands the difference between basic roadside, lockout, fuel delivery, and tow services, routing each appropriately.

The towing industry operates on thin margins with high competition. Speed is the differentiator. Modern Path AI provides the instant response that wins calls while keeping overhead low. Unlike hiring overnight dispatchers—which costs $50,000+ annually per shift—AI provides 24/7 coverage at a fraction of the cost.

Police rotation calls represent significant revenue for towing companies. The AI handles these professionally, collecting badge numbers, incident numbers, and vehicle details for proper documentation. Being reliable on rotation calls builds relationships that lead to more business.

Financially, each captured emergency tow generates $75-$300 depending on distance and vehicle type. Capturing 5 additional calls per day that would have gone to competitors adds substantial annual revenue while maintaining low overhead through AI dispatch.`,
    painHeadline: "Towing is 100% urgency—speed wins every call.",
    painCopy: "A stranded driver is calling every towing company until someone answers and confirms dispatch. Be the company that answers first, every time.",
    leadType: "Tow Dispatch",
    image: "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1200&q=80",
    metaTitle: "Towing AI Voice Agents & Automation | Modern Path AI",
    metaDescription: "Boost your Towing business with 24/7 AI Voice Agents. Automate dispatch, qualify leads, and never miss a call again in the GTA.",
    commonInquiries: [
      "Emergency towing",
      "Flat tire assistance",
      "Jump start service",
      "Locked out of car",
      "Accident towing",
      "Long distance tow",
      "Motorcycle towing",
      "Fuel delivery",
      "Winch out service",
      "Roadside assistance",
      "Heavy duty towing",
      "Impound release"
    ],
    useCases: [
      { title: "Instant Location Collection", description: "AI collects highway, mile markers, cross streets, or parking lot name for fastest dispatch to stranded vehicles.", icon: Zap },
      { title: "Vehicle & Scene Details", description: "Gets year, make, model, and issue—plus accident details if applicable—to dispatch the right equipment.", icon: CheckCircle },
      { title: "Motor Club Handling", description: "Collects member numbers, claim information, and policy details for proper insurance/roadside billing.", icon: Shield },
      { title: "ETA Communication", description: "Provides stranded drivers with estimated arrival based on truck location and current dispatches.", icon: Clock }
    ],
    faq: [
      { q: "How does the AI collect accurate location from stressed drivers?", a: "It asks for highway name and direction first, then mile markers or exits. For parking lots, it asks for business name and entrance. Always confirms before dispatch." },
      { q: "Can it handle police rotation calls?", a: "Yes! The AI collects badge number, incident number, and vehicle details for police impounds and rotation tows with proper documentation." },
      { q: "What about motor club and insurance calls?", a: "The AI handles roadside assistance calls, collecting member numbers, claim information, and vehicle details for proper billing." },
      { q: "How does this compare to overnight dispatch staff?", a: "A single overnight dispatcher costs $50,000+ annually. Modern Path AI provides 24/7 dispatch coverage at a fraction of the cost." },
      { q: "What's the revenue impact?", a: "Each emergency tow is $75-$300. Capturing 5 more daily adds significant revenue while maintaining low AI-powered overhead." }
    ]
  },
  "auto-detailing": {
    title: "Auto Detailing",
    slug: "auto-detailing",
    heroTitle: "24/7 AI Booking for Auto Detailing Businesses",
    heroSubtitle: "Never miss a detail appointment while you're perfecting someone's paint. Our AI answers calls, explains your packages, and books appointments while you work.",
    seoCopy: `The auto detailing industry thrives on convenience and immediate booking. When someone decides to get their car detailed, they want to book NOW—often for the same week. Modern Path AI ensures every inquiry becomes a confirmed appointment.

Our AI understands detailing services and can guide customers through your menu. It explains package differences—express wash, full interior, paint correction, ceramic coating—with pricing so customers can choose confidently. It adjusts quotes based on vehicle type (sedan, SUV, truck) to provide accurate pricing upfront.

Mobile vs. shop scheduling requires different handling. The AI determines customer preference and schedules accordingly—collecting address and access details for mobile service or confirming drop-off times for shop visits. This flexibility captures both customer types without confusion.

Upselling is natural for our AI. When booking a basic detail, it mentions ceramic coating protection. For interior services, it suggests pet hair removal or leather conditioning. These suggestions feel helpful rather than pushy because they're contextually relevant to the customer's vehicle and needs.

Fleet and dealer accounts represent growth opportunity in detailing. The AI handles lot services, collecting vehicle quantities, service frequencies, and dealer contact information. These recurring commercial accounts provide stable revenue beyond consumer bookings.

The detailing industry is highly competitive on convenience. Customers calling multiple shops will book with whoever answers immediately, explains services clearly, and confirms their preferred time. Modern Path AI ensures you're always that shop.`,
    painHeadline: "Detailing customers are ready to book—capture them now.",
    painCopy: "When someone decides to get their car detailed, they want to book immediately. Every call that goes to voicemail is a customer booking with a competitor.",
    leadType: "Detail Appointment",
    image: "https://images.unsplash.com/photo-1607860108855-64acf2078ed9?w=1200&q=80",
    metaTitle: "Auto Detailing AI Voice Agents & Automation | Modern Path AI",
    metaDescription: "Boost your Auto Detailing business with 24/7 AI Voice Agents. Automate scheduling, qualify leads, and never miss a call again in the GTA.",
    commonInquiries: [
      "Full interior detail",
      "Exterior wash and wax",
      "Paint correction",
      "Ceramic coating",
      "Headlight restoration",
      "Pet hair removal",
      "Leather conditioning",
      "Engine bay cleaning",
      "Odor removal",
      "Mobile detailing",
      "New car protection",
      "Fleet detailing"
    ],
    useCases: [
      { title: "Package Explanation", description: "AI explains your detail packages—basic wash, full interior, paint correction—with pricing to help customers choose.", icon: Star },
      { title: "Vehicle Size Pricing", description: "Adjusts quotes based on vehicle type—sedan, SUV, truck—to provide accurate pricing upfront.", icon: CheckCircle },
      { title: "Mobile vs. Shop", description: "Determines customer preference and schedules appropriately—collecting address for mobile or confirming drop-off times.", icon: Calendar },
      { title: "Add-On Services", description: "Naturally suggests ceramic coating, pet hair removal, or leather conditioning based on the service being booked.", icon: Zap }
    ],
    faq: [
      { q: "Can the AI explain different detail packages?", a: "Yes! It knows your full service menu and explains what's included in each—express wash, interior detail, full correction, ceramic coating." },
      { q: "How does it handle vehicle size pricing?", a: "The AI asks about vehicle type (sedan, SUV, truck, van) and adjusts pricing accordingly. It notes factors like pet hair or heavy stains." },
      { q: "What about mobile vs. shop scheduling?", a: "The AI determines preference, collecting address for mobile service or confirming drop-off times and duration for shop visits." },
      { q: "Can it handle fleet and dealer accounts?", a: "Yes! It manages commercial accounts, collecting vehicle quantities, service frequencies, and contacts for dealer lot maintenance." },
      { q: "How does it help with upselling?", a: "It naturally suggests relevant add-ons—ceramic coating for paint services, leather conditioning for interiors—based on context." }
    ]
  },
  "accounting": {
    title: "Accounting Firms",
    slug: "accounting",
    heroTitle: "24/7 AI Booking for CPAs & Accounting Firms",
    heroSubtitle: "Never miss a tax client during busy season again. Our AI handles the flood of January-April calls, qualifying leads and booking appointments while you focus on returns.",
    seoCopy: `Accounting firms face extreme seasonality. From January through April, phones ring constantly while CPAs are buried in tax returns. Every missed call is a potential $500-$5,000 client hiring your competitor. Modern Path AI ensures you capture every lead during your busiest months.

Our AI understands accounting service categories and can route inquiries appropriately. It distinguishes between individual tax preparation, business returns, bookkeeping, payroll services, and advisory consultations. For new clients, it collects prior CPA information, return complexity indicators, and document readiness to prepare for efficient onboarding.

Tax season call handling is uniquely challenging. Clients call with questions about their existing returns, potential clients inquire about services, and everyone wants immediate attention. The AI manages this complexity, routing existing clients for callbacks while qualifying and booking new prospects.

Year-round services build sustainable practices. The AI promotes bookkeeping, payroll, and advisory services to callers who only know you for tax prep. This cross-selling expands client relationships beyond seasonal tax work.

The economics during tax season are compelling. A qualified tax client is worth $500-$2,000 in immediate revenue and potentially more in ongoing services. Missing 5 new client calls per day during busy season costs $50,000+ in lost revenue. Modern Path AI captures these opportunities without adding temporary staff.

Many accounting firms struggle with professional phone presence during busy season. Staff are overwhelmed, hold times increase, and client experience suffers. AI provides consistent, professional response regardless of call volume.`,
    painHeadline: "During tax season, every missed call is lost revenue.",
    painCopy: "Your phone rings off the hook from January to April. Every voicemail is a potential $500-$5,000 client calling your competitor instead.",
    leadType: "Client Consultation",
    image: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=1200&q=80",
    metaTitle: "Accounting AI Voice Agents & Automation | Modern Path AI",
    metaDescription: "Boost your Accounting business with 24/7 AI Voice Agents. Automate scheduling, qualify leads, and never miss a call again in the GTA.",
    commonInquiries: [
      "Tax preparation services",
      "Small business accounting",
      "Bookkeeping services",
      "Payroll processing",
      "Tax planning consultation",
      "QuickBooks setup",
      "Business tax returns",
      "Personal tax filing",
      "IRS audit help",
      "Financial statements",
      "New business setup",
      "Year-end tax planning"
    ],
    useCases: [
      { title: "Tax Season Overflow", description: "Handle 10x call volume during January-April without hiring temporary staff or losing leads.", icon: Zap },
      { title: "Service Qualification", description: "AI identifies if caller needs individual tax prep, business returns, bookkeeping, payroll, or advisory.", icon: CheckCircle },
      { title: "New Client Intake", description: "Collects prior CPA info, return complexity, and document checklist status for efficient onboarding.", icon: Calendar },
      { title: "Year-Round Promotion", description: "Promotes bookkeeping, payroll, and advisory services to expand beyond seasonal tax work.", icon: Star }
    ],
    faq: [
      { q: "How does the AI handle tax season call volume?", a: "Modern Path AI handles unlimited concurrent calls, so even when 50 people call at once during peak season, everyone gets answered immediately." },
      { q: "Can it differentiate between service types?", a: "Yes! It distinguishes individual tax prep, business returns, bookkeeping, payroll services, and advisory consultations to route appropriately." },
      { q: "What about existing clients calling with questions?", a: "The AI identifies existing clients and routes them for callback while qualifying and booking new prospects, managing the complexity of busy season." },
      { q: "How does this compare to tax season temp staff?", a: "Temp staff require training and may make errors. Modern Path AI provides reliable 24/7 coverage at a fixed cost throughout busy season." },
      { q: "What's the revenue impact?", a: "Missing 5 new client calls daily during busy season costs $50,000+ in lost revenue. AI captures these opportunities without added staff." }
    ]
  },
  "real-estate": {
    title: "Real Estate",
    slug: "real-estate",
    heroTitle: "24/7 AI Lead Capture for Real Estate Professionals",
    heroSubtitle: "Never miss a buyer inquiry or listing call again. Our AI captures leads while you're showing properties, qualifying prospects and scheduling showings automatically.",
    seoCopy: `In real estate, the first agent to respond wins the client. A motivated buyer calling about a listing isn't leaving voicemails with multiple agents—they're working with whoever answers first. Modern Path AI ensures you capture every lead, whether you're at a closing, showing, or open house.

Our AI understands real estate terminology and lead qualification. It determines if the caller is buying, selling, or both, along with timeline, budget range, and preferred neighborhoods. For buyer leads, it checks pre-approval status and can schedule showing appointments. For seller leads, it collects property address, reason for selling, and timeline expectations.

Listing inquiries are high-intent leads that require immediate response. The AI can answer questions about your active listings—price, bedrooms, features, availability—and capture buyer information for follow-up. This 24/7 listing coverage ensures you never lose a buyer to slow response.

The real estate industry has embraced technology, but phone calls remain critical for serious buyers and sellers. While younger clients might start with text, high-value transactions typically involve phone conversations. Modern Path AI ensures these crucial calls never go unanswered.

CRM integration maximizes AI effectiveness. Leads flow directly into Follow Up Boss, KvCORE, BoomTown, or other real estate CRMs with full qualification data. Your follow-up automation begins immediately, increasing conversion rates.

The financial impact is significant. The average real estate commission is $15,000+. Capturing one additional client per month through better phone response adds $180,000+ annually to your GCI. Modern Path AI costs a fraction of that first commission.`,
    painHeadline: "In real estate, the first agent to respond wins.",
    painCopy: "A motivated buyer calling about a listing isn't leaving voicemails with multiple agents. They're working with whoever answers first.",
    leadType: "Buyer/Seller Lead",
    image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&q=80",
    metaTitle: "Real Estate AI Voice Agents & Automation | Modern Path AI",
    metaDescription: "Boost your Real Estate business with 24/7 AI Voice Agents. Automate scheduling, qualify leads, and never miss a call again in the GTA.",
    commonInquiries: [
      "Home value estimate",
      "Schedule a showing",
      "First-time homebuyer",
      "Selling my home",
      "Investment property",
      "Pre-approval help",
      "Open house schedule",
      "Condo listings",
      "Luxury homes",
      "Commercial property",
      "Relocation services",
      "Market analysis"
    ],
    useCases: [
      { title: "Lead Qualification", description: "AI determines if caller is buying, selling, or both—plus timeline, budget range, and preferred neighborhoods.", icon: CheckCircle },
      { title: "Showing Scheduling", description: "Books property showings directly into your calendar while you're with other clients.", icon: Calendar },
      { title: "Listing Inquiries", description: "Answers questions about your listings 24/7—price, bedrooms, features—and captures buyer information.", icon: Zap },
      { title: "CRM Integration", description: "Leads flow directly to Follow Up Boss, KvCORE, BoomTown with full qualification data.", icon: Shield }
    ],
    faq: [
      { q: "How does the AI qualify real estate leads?", a: "It asks about buying vs. selling, timeline, budget/price expectations, preferred neighborhoods, and pre-approval status to score lead quality." },
      { q: "Can it handle multiple listings?", a: "Yes! The AI can answer questions about all your active listings and schedule showings for specific properties." },
      { q: "Will serious buyers trust an AI?", a: "Motivated buyers want information fast. An AI that answers immediately with listing details and books a showing beats a voicemail that might not be returned for hours." },
      { q: "Does it integrate with real estate CRMs?", a: "Modern Path AI integrates with Follow Up Boss, KvCORE, BoomTown, and other real estate CRMs for automatic lead entry." },
      { q: "What's the ROI?", a: "Average commission is $15,000+. Capturing one additional client monthly adds $180,000+ annually—far exceeding AI cost." }
    ]
  },
  "beauty": {
    title: "Beauty & Wellness",
    slug: "beauty",
    heroTitle: "24/7 AI Booking for Salons & Spas",
    heroSubtitle: "Never miss a booking while you're with a client. Our AI answers calls, checks your real availability, and books appointments—including the right stylist and service time.",
    seoCopy: `In the beauty industry, convenience wins the client. When someone decides they need a haircut, massage, or facial, they want to book NOW. Every call that rings while you're mid-service is a potential client booking with a competitor. Modern Path AI ensures every call becomes a booking.

Our AI integrates with salon booking software for real-time availability. It checks actual open slots before confirming appointments, preventing double-bookings and the awkward rescheduling calls that damage client relationships. For multi-stylist salons, it matches clients with stylists based on service expertise and preference.

Service guidance is valuable for new clients. The AI can explain your service menu, answer pricing questions, and recommend appropriate services based on client needs. This pre-visit consultation builds confidence and reduces no-shows.

Waitlist management keeps revenue flowing. When your schedule is full, the AI adds clients to your waitlist and can automatically notify them when cancellations occur. This feature turns potential lost revenue into booked appointments.

The salon industry faces unique staffing challenges. Front desk staff are expensive, often unavailable during peak calling hours, and create bottlenecks during busy periods. Modern Path AI provides consistent, professional response 24/7 at a fraction of staffing costs.

Client lifetime value in beauty services is substantial. A loyal client visiting monthly for years represents thousands in revenue. Every missed call is potentially missing that entire relationship. Modern Path AI ensures you capture every opportunity for long-term client relationships.`,
    painHeadline: "In beauty, convenience wins the client.",
    painCopy: "When someone decides they need a haircut or facial, they want to book NOW. Every call that rings mid-service is a client booking with a competitor.",
    leadType: "Appointment Booking",
    image: "https://images.unsplash.com/photo-1560066984-138dadb4c035?w=1200&q=80",
    metaTitle: "Beauty & Wellness AI Voice Agents & Automation | Modern Path AI",
    metaDescription: "Boost your Beauty & Wellness business with 24/7 AI Voice Agents. Automate scheduling, qualify leads, and never miss a call again in the GTA.",
    commonInquiries: [
      "Haircut appointment",
      "Hair color service",
      "Balayage highlights",
      "Facial treatment",
      "Massage booking",
      "Manicure and pedicure",
      "Waxing services",
      "Bridal hair and makeup",
      "Eyelash extensions",
      "Keratin treatment",
      "Men's grooming",
      "Spa package"
    ],
    useCases: [
      { title: "Real-Time Booking", description: "AI checks actual availability and books appointments instantly—no double-bookings, no callbacks needed.", icon: Calendar },
      { title: "Stylist Matching", description: "Books with specific stylists based on client preference, availability, and service expertise.", icon: Users },
      { title: "Service Guidance", description: "Explains your service menu, answers pricing questions, and recommends appropriate services.", icon: Star },
      { title: "Waitlist Management", description: "When full, adds clients to waitlist and notifies them automatically when cancellations occur.", icon: Clock }
    ],
    faq: [
      { q: "How does the AI know my availability?", a: "Modern Path AI syncs with your booking software (Vagaro, Fresha, Square Appointments, GlossGenius) in real-time, only offering actually available slots." },
      { q: "Can it handle multiple stylists?", a: "Yes! It books for multiple team members based on their individual availability, specialties, and service durations." },
      { q: "What about service pricing questions?", a: "The AI knows your full service menu and pricing—cuts, color, treatments, packages—so clients get accurate quotes before booking." },
      { q: "How does waitlist management work?", a: "When your schedule is full, the AI adds clients to a waitlist. When cancellations occur, it can automatically notify waiting clients." },
      { q: "What's the cost compared to front desk staff?", a: "A full-time receptionist costs $30,000+ annually. Modern Path AI provides 24/7 coverage at a fraction of the cost." }
    ]
  },
  "pet-care": {
    title: "Pet Care",
    slug: "pet-care",
    heroTitle: "24/7 AI Booking for Pet Groomers & Veterinarians",
    heroSubtitle: "Never miss a grooming appointment or new patient call again. Our AI handles bookings while you're caring for someone's fur baby.",
    seoCopy: `Pet parents are passionate—and impatient. When someone's dog needs grooming or their cat is sick, they're calling until someone answers. They're not leaving voicemails—they're booking with the first groomer or vet who picks up. Modern Path AI ensures you capture every pet parent call.

Our AI understands pet care terminology and collects appropriate information. For grooming, it gathers pet name, breed, size, coat condition, and any special requirements (anxiety, aggression, medical conditions). For veterinary appointments, it distinguishes between wellness visits and potential emergencies.

Pet profiles make repeat bookings efficient. The AI remembers returning clients and their pets, referencing previous services and preferences. This personalization builds loyalty and makes booking faster for regular clients.

Emergency triage is critical for veterinary practices. When a pet parent describes symptoms—difficulty breathing, poisoning, trauma—the AI recognizes emergency indicators and can connect directly to your on-call vet or direct to emergency services. This life-saving protocol protects pets and your practice.

The pet care industry continues growing as pet ownership increases and owners spend more on their animals. Grooming appointments average $60-$100, and veterinary visits average $200-$300. Consistent booking capture through AI drives significant revenue growth.

Multi-pet households represent additional opportunity. When booking for one pet, the AI can check if other pets in the household are due for services, increasing average transaction value.`,
    painHeadline: "Pet parents are passionate—and impatient.",
    painCopy: "When someone's dog needs grooming or their cat is sick, they're calling until someone answers. They're booking with whoever picks up first.",
    leadType: "Pet Appointment",
    image: "https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=1200&q=80",
    metaTitle: "Pet Care AI Voice Agents & Automation | Modern Path AI",
    metaDescription: "Boost your Pet Care business with 24/7 AI Voice Agents. Automate scheduling, qualify leads, and never miss a call again in the GTA.",
    commonInquiries: [
      "Dog grooming appointment",
      "Cat grooming",
      "Pet vaccination",
      "Sick pet visit",
      "Puppy wellness exam",
      "Pet dental cleaning",
      "Nail trimming",
      "Pet boarding",
      "Doggy daycare",
      "Emergency vet visit",
      "Flea treatment",
      "Spay and neuter"
    ],
    useCases: [
      { title: "Pet Profile Collection", description: "AI gathers pet name, breed, size, and special needs—anxiety, medical conditions, grooming preferences.", icon: CheckCircle },
      { title: "Service Booking", description: "Books grooming, boarding, daycare, or vet appointments based on your actual availability.", icon: Calendar },
      { title: "Emergency Triage", description: "For vet clinics, identifies emergencies—poisoning, injury, breathing difficulty—for immediate attention.", icon: Zap },
      { title: "Multi-Pet Households", description: "Checks if other pets are due for services when booking, increasing average transaction value.", icon: Star }
    ],
    faq: [
      { q: "Can the AI handle multiple pet types?", a: "Yes! It asks about pet species, breed, size, and age to book appropriate appointment times and flag special requirements." },
      { q: "How does it handle grooming preferences?", a: "The AI asks about desired cut style, matting concerns, skin sensitivities, and grooming anxiety so your team can prepare appropriately." },
      { q: "What about veterinary emergencies?", a: "The AI is trained to identify emergency symptoms and can immediately connect to your on-call vet or direct to emergency services." },
      { q: "Does it remember returning clients?", a: "Yes! The AI references previous services and pet preferences for returning clients, making repeat bookings faster and more personal." },
      { q: "What's the revenue impact?", a: "Grooming averages $60-$100, vet visits $200-$300. Consistent booking capture through AI drives significant growth." }
    ]
  },
  "dental": {
    title: "Dental Clinics",
    slug: "dental",
    heroTitle: "24/7 AI Booking for Dental Practices",
    heroSubtitle: "Never miss a new patient call or emergency toothache inquiry again. Our AI answers in 2 seconds, books appointments, and captures insurance information while you're chairside.",
    seoCopy: `In dentistry, the first practice to answer wins the patient. A patient with a toothache isn't leaving voicemails—they're calling practices until someone books them in. Someone comparing dentists will choose the practice that answers immediately and schedules them efficiently. Modern Path AI ensures you're always that practice.

Our AI understands dental terminology and appointment types. It distinguishes between emergencies (trauma, severe pain, swelling), new patient exams, hygiene appointments, and specific procedures. This categorization allows appropriate scheduling and priority assignment.

Insurance pre-collection streamlines patient intake. The AI gathers insurance provider, member ID, and group number before the appointment, allowing your team to verify benefits in advance. This reduces day-of-appointment surprises and improves patient experience.

New patient acquisition is the growth engine for dental practices. The AI collects not just appointment information, but also basic medical history, current medications, and dental concerns. This comprehensive intake means new patients arrive with paperwork essentially complete.

Cancellation recovery protects revenue. When patients cancel, the AI can automatically reach out to your waitlist to fill valuable hygiene and treatment slots. This feature alone often pays for AI implementation by recovering otherwise lost revenue.

The economics are compelling. A new dental patient has a lifetime value of $2,000-$5,000+. Missing new patient calls because staff are with patients or it's after hours costs substantial long-term revenue. Modern Path AI ensures every potential patient becomes an actual patient.`,
    painHeadline: "In dentistry, the first practice to answer wins.",
    painCopy: "A patient with a toothache isn't leaving voicemails. Someone comparing dentists will choose the practice that answers immediately and schedules efficiently.",
    leadType: "Patient Appointment",
    image: "https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=1200&q=80",
    metaTitle: "Dental AI Voice Agents & Automation | Modern Path AI",
    metaDescription: "Boost your Dental business with 24/7 AI Voice Agents. Automate scheduling, qualify leads, and never miss a call again in the GTA.",
    commonInquiries: [
      "Toothache emergency",
      "Teeth cleaning",
      "New patient exam",
      "Dental crown",
      "Teeth whitening",
      "Root canal treatment",
      "Wisdom tooth extraction",
      "Invisalign consultation",
      "Dental implants",
      "Chipped tooth repair",
      "Cavity filling",
      "Pediatric dentist"
    ],
    useCases: [
      { title: "Emergency Triage", description: "AI determines true emergencies (trauma, severe pain, swelling) vs. routine issues for proper scheduling priority.", icon: Zap },
      { title: "Insurance Pre-Collection", description: "Gathers insurance provider, member ID, and group number so your team can verify benefits before the appointment.", icon: Shield },
      { title: "New Patient Intake", description: "Collects medical history basics, current medications, and dental concerns before their first visit.", icon: CheckCircle },
      { title: "Cancellation Recovery", description: "When patients cancel, automatically reaches out to your waitlist to fill valuable hygiene and treatment slots.", icon: Calendar }
    ],
    faq: [
      { q: "How does the AI handle dental emergencies?", a: "It asks about pain level, symptoms (swelling, bleeding, trauma), and duration to determine urgency and schedule appropriately." },
      { q: "Can it collect insurance information?", a: "Yes! It gathers insurance provider, member ID, and group number before the appointment for advance benefit verification." },
      { q: "What about new patient paperwork?", a: "The AI collects medical history, medications, and dental concerns so new patients arrive with intake essentially complete." },
      { q: "Does it integrate with dental software?", a: "Modern Path AI integrates with Dentrix, Eaglesoft, Open Dental, and other practice management systems for seamless scheduling." },
      { q: "What's the lifetime value impact?", a: "A new dental patient is worth $2,000-$5,000+. Capturing every new patient inquiry through AI drives significant long-term revenue." }
    ]
  },
  "fitness": {
    title: "Fitness Studios",
    slug: "fitness",
    heroTitle: "24/7 AI Booking for Gyms & Fitness Studios",
    heroSubtitle: "Never miss a membership inquiry or class booking again. Our AI answers calls while you're coaching, booking new members and class registrations automatically.",
    seoCopy: `Fitness motivation is fleeting—capture it immediately. When someone decides to join a gym or try a new class, they're ready to commit NOW. A call that goes to voicemail often means a lost member—they'll sign up with the competitor who answers. Modern Path AI ensures every inquiry becomes a membership.

Our AI understands fitness service categories and can guide prospects effectively. It explains membership options (monthly, annual, class packages, premium tiers) with pricing and included amenities. It answers facility questions and can schedule tours for prospects who want to see the space before joining.

Class booking requires real-time availability checking. The AI accesses your class schedule and books members into yoga, spin, HIIT, CrossFit, and other classes based on actual open spots. This prevents overbooking and the frustration of turning away members at the door.

Personal training upsell represents significant revenue opportunity. When booking new memberships or class packages, the AI introduces personal training options, matching prospects with trainers based on goals, availability, and specialty areas. This natural upsell increases average revenue per member.

The gym industry has high member acquisition costs. Marketing, advertising, and promotions drive leads to your phone. Wasting those investments by missing calls erases marketing ROI. Modern Path AI ensures every marketing dollar spent generates actual member conversations.

Retention starts with the first interaction. Members who have a smooth, professional booking experience are more likely to stay long-term. AI provides consistent, positive first impressions regardless of how busy your staff might be.`,
    painHeadline: "Fitness motivation is fleeting—capture it immediately.",
    painCopy: "When someone decides to join a gym, they're ready to commit NOW. A call that goes to voicemail means they'll sign up with the competitor who answers.",
    leadType: "Membership Inquiry",
    image: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1200&q=80",
    metaTitle: "Fitness AI Voice Agents & Automation | Modern Path AI",
    metaDescription: "Boost your Fitness business with 24/7 AI Voice Agents. Automate scheduling, qualify leads, and never miss a call again in the GTA.",
    commonInquiries: [
      "Gym membership pricing",
      "Free trial class",
      "Personal trainer rates",
      "Yoga class schedule",
      "Cancel membership",
      "Group fitness classes",
      "Weight loss program",
      "Gym tour appointment",
      "24-hour gym access",
      "Senior fitness classes",
      "Kids programs",
      "Corporate membership"
    ],
    useCases: [
      { title: "Membership Explanation", description: "AI explains membership options—monthly, annual, class packages, premium—with pricing and included amenities.", icon: Star },
      { title: "Class Booking", description: "Accesses your class schedule and books members into yoga, spin, HIIT, and other classes based on real-time availability.", icon: Calendar },
      { title: "Personal Training Upsell", description: "Introduces PT options during membership or class booking, matching prospects with trainers by goals and availability.", icon: Users },
      { title: "Tour Scheduling", description: "Books facility tours for prospects who want to see the gym before joining.", icon: CheckCircle }
    ],
    faq: [
      { q: "Can the AI explain different membership levels?", a: "Yes! It knows your full membership structure and explains what's included in each level to help prospects choose the right fit." },
      { q: "How does class booking work?", a: "The AI accesses your class schedule in real-time and books members into available spots, preventing overbooking." },
      { q: "What about personal training leads?", a: "The AI asks about fitness goals, experience level, and scheduling preferences to match prospects with appropriate trainers." },
      { q: "Does it help with new member onboarding?", a: "Yes! It can schedule orientation sessions, PT consultations, and first classes as part of the membership signup process." },
      { q: "What's the member acquisition impact?", a: "Every missed call wastes marketing spend. AI ensures every inquiry becomes a conversation, maximizing marketing ROI." }
    ]
  }
};

// Related industries mapping for internal linking
const relatedIndustries = {
  "appliance-repair": ["home-services", "garage-door", "pest-control"],
  "home-services": ["appliance-repair", "garage-door", "landscaping"],
  "legal": ["accounting", "real-estate", "auto-repair"],
  "garage-door": ["appliance-repair", "locksmith", "home-services"],
  "locksmith": ["garage-door", "auto-repair", "towing"],
  "pest-control": ["landscaping", "appliance-repair", "home-services"],
  "landscaping": ["pest-control", "home-services", "real-estate"],
  "auto-repair": ["towing", "auto-detailing", "locksmith"],
  "towing": ["auto-repair", "locksmith", "auto-detailing"],
  "auto-detailing": ["auto-repair", "towing", "beauty"],
  "accounting": ["legal", "real-estate", "fitness"],
  "real-estate": ["legal", "accounting", "landscaping"],
  "beauty": ["pet-care", "dental", "fitness"],
  "pet-care": ["beauty", "dental", "fitness"],
  "dental": ["beauty", "pet-care", "fitness"],
  "fitness": ["beauty", "dental", "pet-care"]
};

// Comparison table component
const ComparisonTable = () => (
  <div className="overflow-x-auto">
    <table className="w-full border-collapse">
      <thead>
        <tr className="border-b border-white/10">
          <th className="text-left py-4 px-4 text-sm font-medium text-[#A0A0A0]">Feature</th>
          <th className="text-left py-4 px-4 text-sm font-medium text-[#A0A0A0]">Traditional Answering Service</th>
          <th className="text-left py-4 px-4 text-sm font-medium text-[#00FFFF]">Modern Path AI</th>
        </tr>
      </thead>
      <tbody>
        <tr className="border-b border-white/5">
          <td className="py-4 px-4 text-sm">Response Speed</td>
          <td className="py-4 px-4 text-sm text-[#A0A0A0]">30-60 second hold times</td>
          <td className="py-4 px-4 text-sm text-[#00FFFF]">Instant Answer (2 seconds)</td>
        </tr>
        <tr className="border-b border-white/5">
          <td className="py-4 px-4 text-sm">Action Taken</td>
          <td className="py-4 px-4 text-sm text-[#A0A0A0]">Takes a message (passive)</td>
          <td className="py-4 px-4 text-sm text-[#00FFFF]">Books the job (active)</td>
        </tr>
        <tr className="border-b border-white/5">
          <td className="py-4 px-4 text-sm">Brand Consistency</td>
          <td className="py-4 px-4 text-sm text-[#A0A0A0]">Varies by operator</td>
          <td className="py-4 px-4 text-sm text-[#00FFFF]">100% consistent every call</td>
        </tr>
        <tr>
          <td className="py-4 px-4 text-sm">Availability</td>
          <td className="py-4 px-4 text-sm text-[#A0A0A0]">Limited hours, holiday closures</td>
          <td className="py-4 px-4 text-sm text-[#00FFFF]">24/7/365 Always On</td>
        </tr>
      </tbody>
    </table>
  </div>
);

// Generate FAQ Schema JSON-LD
const generateFAQSchema = (faq) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faq.map(item => ({
      "@type": "Question",
      "name": item.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.a
      }
    }))
  };
};

// Generate enhanced Service Schema JSON-LD
const generateServiceSchema = (data) => {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": `https://www.modernpathai.com/industries/${data.slug}#service`,
    "name": `AI Voice Agent for ${data.title}`,
    "description": data.heroSubtitle,
    "serviceType": `AI Voice Agent for ${data.title}`,
    "provider": {
      "@type": "ProfessionalService",
      "@id": "https://www.modernpathai.com/#organization",
      "name": "Modern Path AI",
      "legalName": "Modern Path AI Inc.",
      "url": "https://www.modernpathai.com",
      "logo": "https://www.modernpathai.com/modern-path-ai-logo.png",
      "telephone": "+1-647-370-7545",
      "email": "info@modernpathai.com",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Toronto",
        "addressRegion": "Ontario",
        "postalCode": "M5V",
        "addressCountry": "CA"
      }
    },
    "areaServed": [
      {
        "@type": "Country",
        "name": "United States"
      },
      {
        "@type": "Country",
        "name": "Canada"
      },
      {
        "@type": "Place",
        "name": "Greater Toronto Area",
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "43.6532",
          "longitude": "-79.3832"
        }
      }
    ],
    "serviceOutput": {
      "@type": "Thing",
      "name": "Automated Lead Capture and Appointment Booking"
    },
    "termsOfService": "https://www.modernpathai.com/privacy",
    "category": "Business Automation",
    "audience": {
      "@type": "BusinessAudience",
      "audienceType": data.title,
      "geographicArea": {
        "@type": "AdministrativeArea",
        "name": "North America"
      }
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": `AI Voice Agent Solutions for ${data.title}`,
      "itemListElement": data.useCases.map((useCase, idx) => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": useCase.title,
          "description": useCase.description
        }
      }))
    }
  };
};

// Industry Landing Page Component
const IndustryPage = () => {
  const { industry } = useParams();
  const data = industryData[industry];
  
  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [industry]);
  
  if (!data) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-[#F0F2F5]">
        <Header />
        <div className="pt-32 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>Page Not Found</h1>
            <p className="text-[#A0A0A0] mb-6">The industry page you're looking for doesn't exist.</p>
            <Link to="/" className="text-[#00FFFF] hover:underline inline-flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Return to Homepage
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const faqSchema = generateFAQSchema(data.faq);
  const serviceSchema = generateServiceSchema(data);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F0F2F5]">
      {/* SEO Meta Tags & Schema */}
      <Helmet>
        <title>{data.metaTitle}</title>
        <meta name="description" content={data.metaDescription} />
        <meta property="og:title" content={data.metaTitle} />
        <meta property="og:description" content={data.metaDescription} />
        <meta property="og:image" content={data.image} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://www.modernpathai.com/industries/${data.slug}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={data.metaTitle} />
        <meta name="twitter:description" content={data.metaDescription} />
        <link rel="canonical" href={`https://www.modernpathai.com/industries/${data.slug}`} />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
      </Helmet>

      {/* Header */}
      <Header />

      {/* Breadcrumbs */}
      <div className="pt-20">
        <Breadcrumbs industry={data.slug} industryTitle={data.title} />
      </div>

      {/* Sticky Mobile CTA */}
      <StickyMobileCTA />

      {/* Hero Section */}
      <section className="pt-12 pb-20 px-4 relative overflow-hidden" data-testid="industry-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-[#00FFFF]/5 to-transparent" />
        <div className="max-w-[1000px] mx-auto text-center relative">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} className="text-sm font-mono uppercase tracking-[0.3em] text-[#00FFFF] mb-6">
              Voice AI for {data.title}
            </motion.p>
            <motion.h1 
              variants={fadeUp}
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6 leading-tight"
              style={{ fontFamily: 'Syne, sans-serif' }}
              data-testid="industry-h1"
            >
              {data.heroTitle}
            </motion.h1>
            <motion.p variants={fadeUp} className="text-base md:text-lg lg:text-xl text-[#A0A0A0] mb-8 max-w-3xl mx-auto leading-relaxed">
              {data.heroSubtitle}
            </motion.p>
            <motion.div variants={fadeUp}>
              <a
                href={CALENDLY_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-[#00FFFF] text-[#0A0A0A] px-8 py-4 rounded-sm font-bold uppercase tracking-wider hover:bg-white transition-colors"
                data-testid="hero-cta-btn"
              >
                Get My Custom Voice Agent Demo
                <ArrowRight className="w-5 h-5" />
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Hero Image */}
      <section className="px-4 pb-20">
        <div className="max-w-[1200px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-2xl overflow-hidden"
          >
            <img 
              src={data.image} 
              alt={`Voice AI for ${data.title} - Modern Path AI automation`}
              className="w-full h-[300px] md:h-[400px] object-cover"
              loading="eager"
            />
          </motion.div>
        </div>
      </section>

      {/* SEO Copy Section */}
      <section className="py-20 px-4 bg-[#0D0D0D]" data-testid="seo-copy-section">
        <div className="max-w-[900px] mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.p variants={fadeUp} className="text-sm font-mono uppercase tracking-[0.3em] text-[#00FFFF] mb-4">
              The Challenge
            </motion.p>
            <motion.h2 
              variants={fadeUp}
              className="text-2xl md:text-3xl lg:text-4xl font-bold mb-6"
              style={{ fontFamily: 'Syne, sans-serif' }}
            >
              {data.painHeadline}
            </motion.h2>
            <motion.div variants={fadeUp} className="prose prose-lg prose-invert max-w-none">
              {data.seoCopy.split('\n\n').map((paragraph, idx) => (
                <p key={idx} className="text-[#A0A0A0] leading-relaxed mb-4">
                  {paragraph}
                </p>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Common Inquiries Section */}
      {data.commonInquiries && data.commonInquiries.length > 0 && (
        <section className="py-16 px-4" data-testid="common-inquiries-section">
          <div className="max-w-[1000px] mx-auto">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
            >
              <motion.p variants={fadeUp} className="text-sm font-mono uppercase tracking-[0.3em] text-[#00FFFF] mb-4 text-center">
                What Callers Ask
              </motion.p>
              <motion.h2 
                variants={fadeUp}
                className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 text-center"
                style={{ fontFamily: 'Syne, sans-serif' }}
              >
                Common Inquiries Our AI Can Handle
              </motion.h2>
              <motion.p variants={fadeUp} className="text-[#A0A0A0] text-center mb-10 max-w-2xl mx-auto">
                Our Voice AI is trained to professionally handle these common {data.title.toLowerCase()} inquiries and many more.
              </motion.p>
              
              <motion.div 
                variants={fadeUp} 
                className="flex flex-wrap justify-center gap-3"
              >
                {data.commonInquiries.map((inquiry, idx) => (
                  <span 
                    key={idx}
                    className="inline-flex items-center px-4 py-2 bg-white/[0.03] border border-white/10 rounded-full text-sm text-[#F0F2F5] hover:border-[#00FFFF]/40 hover:bg-[#00FFFF]/5 transition-all cursor-default"
                    data-testid={`inquiry-tag-${idx}`}
                  >
                    <Phone className="w-3.5 h-3.5 mr-2 text-[#00FFFF]" />
                    {inquiry}
                  </span>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Use Cases Section */}
      <section className="py-20 px-4" data-testid="use-cases-section">
        <div className="max-w-[1200px] mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.p variants={fadeUp} className="text-sm font-mono uppercase tracking-[0.3em] text-[#00FFFF] mb-4 text-center">
              The Solution
            </motion.p>
            <motion.h2 
              variants={fadeUp}
              className="text-2xl md:text-3xl lg:text-4xl font-bold mb-12 text-center"
              style={{ fontFamily: 'Syne, sans-serif' }}
            >
              How Modern Path AI Helps {data.title}
            </motion.h2>
            
            <motion.div variants={fadeUp} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {data.useCases.map((useCase, idx) => {
                const IconComponent = useCase.icon;
                return (
                  <div 
                    key={idx}
                    className="bg-white/[0.02] border border-white/10 rounded-xl p-6 hover:border-[#00FFFF]/30 transition-colors"
                    data-testid={`use-case-${idx}`}
                  >
                    <div className="w-12 h-12 rounded-full bg-[#00FFFF]/10 flex items-center justify-center mb-4">
                      <IconComponent className="w-6 h-6 text-[#00FFFF]" />
                    </div>
                    <h3 className="text-lg font-bold mb-3" style={{ fontFamily: 'Syne, sans-serif' }}>
                      {useCase.title}
                    </h3>
                    <p className="text-[#A0A0A0] text-sm leading-relaxed">{useCase.description}</p>
                  </div>
                );
              })}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Comparison Table Section */}
      <section className="py-20 px-4 bg-[#0D0D0D]" data-testid="comparison-section">
        <div className="max-w-[900px] mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.p variants={fadeUp} className="text-sm font-mono uppercase tracking-[0.3em] text-[#00FFFF] mb-4 text-center">
              Why AI Wins
            </motion.p>
            <motion.h2 
              variants={fadeUp}
              className="text-2xl md:text-3xl lg:text-4xl font-bold mb-8 text-center"
              style={{ fontFamily: 'Syne, sans-serif' }}
            >
              AI vs. Traditional Answering Service
            </motion.h2>
            <motion.div variants={fadeUp} className="bg-white/[0.02] border border-white/10 rounded-xl overflow-hidden">
              <ComparisonTable />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4" data-testid="faq-section" itemScope itemType="https://schema.org/FAQPage">
        <div className="max-w-[900px] mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.p variants={fadeUp} className="text-sm font-mono uppercase tracking-[0.3em] text-[#00FFFF] mb-4 text-center">
              FAQ
            </motion.p>
            <motion.h2 
              variants={fadeUp}
              className="text-2xl md:text-3xl lg:text-4xl font-bold mb-12 text-center"
              style={{ fontFamily: 'Syne, sans-serif' }}
            >
              Common Questions About Voice AI for {data.title}
            </motion.h2>
            
            <motion.div variants={fadeUp} className="space-y-4">
              {data.faq.map((item, idx) => (
                <div 
                  key={idx}
                  className="bg-white/[0.02] border border-white/10 rounded-xl p-6 hover:border-[#00FFFF]/20 transition-colors"
                  data-testid={`faq-item-${idx}`}
                  itemScope
                  itemProp="mainEntity"
                  itemType="https://schema.org/Question"
                >
                  <h3 className="text-base md:text-lg font-bold mb-3 flex items-start gap-3" itemProp="name">
                    <span className="text-[#00FFFF] shrink-0 font-mono text-sm">Q{idx + 1}:</span>
                    <span>{item.q}</span>
                  </h3>
                  <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                    <p className="text-[#A0A0A0] pl-10 leading-relaxed" itemProp="text">{item.a}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-[#0A0A0A] to-[#0D0D0D]" data-testid="final-cta-section">
        <div className="max-w-[800px] mx-auto text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
          >
            <motion.p variants={fadeUp} className="text-sm font-mono uppercase tracking-[0.3em] text-[#00FFFF] mb-4">
              Get Started
            </motion.p>
            <motion.h2 
              variants={fadeUp}
              className="text-2xl md:text-4xl lg:text-5xl font-bold mb-6"
              style={{ fontFamily: 'Syne, sans-serif' }}
            >
              Ready to Stop Missing {data.leadType}s?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-base md:text-lg text-[#A0A0A0] mb-8 max-w-2xl mx-auto">
              Get a custom Voice AI agent built specifically for your {data.title.toLowerCase()} business. See exactly how it handles your calls in a free demo.
            </motion.p>
            <motion.div variants={fadeUp}>
              <a
                href={CALENDLY_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-[#00FFFF] text-[#0A0A0A] px-10 py-5 rounded-sm font-bold uppercase tracking-wider hover:bg-white transition-colors text-base md:text-lg shadow-[0_0_30px_rgba(0,255,255,0.3)]"
                data-testid="final-cta-btn"
              >
                Schedule Your Free Demo
                <ArrowRight className="w-5 h-5" />
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Related Industries Section */}
      {relatedIndustries[data.slug] && (
        <section className="py-16 px-4 bg-[#0D0D0D]" data-testid="related-industries-section">
          <div className="max-w-[1000px] mx-auto">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
            >
              <motion.p variants={fadeUp} className="text-sm font-mono uppercase tracking-[0.3em] text-[#00FFFF] mb-4 text-center">
                Explore More
              </motion.p>
              <motion.h2 
                variants={fadeUp}
                className="text-2xl md:text-3xl font-bold mb-10 text-center"
                style={{ fontFamily: 'Syne, sans-serif' }}
              >
                Related Industries We Serve
              </motion.h2>
              
              <motion.div 
                variants={fadeUp} 
                className="grid grid-cols-1 md:grid-cols-3 gap-4"
              >
                {relatedIndustries[data.slug].map((relatedSlug) => {
                  const relatedData = industryData[relatedSlug];
                  if (!relatedData) return null;
                  return (
                    <Link 
                      key={relatedSlug}
                      to={`/industries/${relatedSlug}`}
                      className="group bg-white/[0.02] border border-white/10 rounded-xl p-6 hover:border-[#00FFFF]/50 hover:bg-[#00FFFF]/5 transition-all"
                      data-testid={`related-industry-${relatedSlug}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-bold mb-1 group-hover:text-[#00FFFF] transition-colors" style={{ fontFamily: 'Syne, sans-serif' }}>
                            {relatedData.title}
                          </h3>
                          <p className="text-sm text-[#A0A0A0]">
                            Voice AI for {relatedData.title}
                          </p>
                        </div>
                        <ArrowRight className="w-5 h-5 text-[#A0A0A0] group-hover:text-[#00FFFF] group-hover:translate-x-1 transition-all" />
                      </div>
                    </Link>
                  );
                })}
              </motion.div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Footer with Our Solutions column */}
      <GlobalFooter variant="simple" />
    </div>
  );
};

export default IndustryPage;
export { industryData };
